import React, { useState, useEffect } from 'react';
import apiClient from '../utils/apiClient';
import Select from 'react-select';
import { SERVER_BASE } from '../config/server';
import { useTable, useSortBy, usePagination, useGlobalFilter } from 'react-table';
import * as XLSX from 'xlsx';
import 'bootstrap/dist/css/bootstrap.min.css';
import Swal from 'sweetalert2';
import DateRangePickerComponent from './CustomDateRangePicker';
import moment from 'moment';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const EventWiseReport = ({ accessOfDepartments }) => {
  const [departments, setDepartments] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedDept, setSelectedDept] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const code = localStorage.getItem('code');
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (accessOfDepartments) {
      const deptArray = accessOfDepartments
        .split(',')
        .map((dept) => ({ value: dept.trim(), label: dept.trim() }));
      setDepartments(deptArray);
    }
  }, [accessOfDepartments]);
  const fetchEvents = async () => {
    try {
      const response = await apiClient.get(SERVER_BASE + "/getpurposesInventory", {
        params: { code },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const eventData = response.data;

      const seen = new Set();
      const uniqueEvents = [];

      for (const dept of eventData) {
        const normalized = dept.purposeName?.trim().toLowerCase(); // Normalize for comparison
        if (!seen.has(normalized)) {
          seen.add(normalized);
          uniqueEvents.push({
            value: dept.purposeName,
            label: dept.purposeName,
          });
        }
      }

      setEvents(uniqueEvents);

    } catch (error) {
      console.error("Error fetching events:", error);
    }
  };
  useEffect(() => {
    fetchEvents();
  }, []);

  const getReport = async () => {
    if (!selectedDept || selectedDept.length === 0) {
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'warning',
        title: 'Please select at least one department',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      return;
    }
    if (!selectedEvent) {
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'warning',
        title: 'Please select event',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      return;
    }

    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'warning',
        title: 'Please select a valid date range',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      return;
    }

    setLoading(true);

    try {
      const [startDate, endDate] = dateRange;
      const departmentNames = selectedDept.map((dept) => dept.value);
      const res = await apiClient.get(SERVER_BASE + '/getEventWiseInventoryReport', {
        params: {
          departments: departmentNames.join(','),
          event: selectedEvent.value,
          startDate: startDate,
          endDate: endDate,
        },
      });

      setReportData(res.data.report);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  const columns = React.useMemo(() => [
    {
      Header: "S.No",
      Cell: ({ row }) => row.index + 1,
    },
    { Header: 'Item', accessor: 'printName' },
    // { Header: 'Opening Stock', accessor: 'openingStock' },
    { Header: 'Addition', accessor: 'totalAdd' },
    { Header: 'Deduction', accessor: 'totalLess' },
    // {
    //   Header: 'Closing Stock',
    //   accessor: row =>
    //     (Number(row.openingStock || 0) + Number(row.totalAdd || 0) - Number(row.totalLess || 0)).toFixed(2),
    //   id: 'closingStock',
    // },
    { Header: 'UOM', accessor: 'uom' },
  ], []);

  const {
    getTableProps, getTableBodyProps, headerGroups, page,
    prepareRow, setGlobalFilter, state, nextPage, previousPage,
    canNextPage, canPreviousPage, pageOptions, setPageSize
  } = useTable(
    { columns, data: reportData, initialState: { pageSize: 10 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  const exportToExcel = async () => {
    if (!Array.isArray(reportData)) {
      console.error("reportData is not an array:", reportData);
      alert("No data available to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Event Wise Report');

    // Top merged label rows
    worksheet.mergeCells('A1:F1');
    worksheet.getCell('A1').value = `Status: ${selectedEvent?.value || 'All'}`;
    worksheet.getCell('A1').font = { bold: true };

    worksheet.mergeCells('A2:F2');
    worksheet.getCell('A2').value = `Departments: ${selectedDept.map(d => d.value).join(', ') || 'All Departments'}`;
    worksheet.getCell('A2').font = { bold: true };

    worksheet.mergeCells('A3:F3');
    const [startDate, endDate] = dateRange;
    worksheet.getCell('A3').value = `Date Range: ${moment(startDate).format('DD-MMM-YY') || 'N/A'} to ${moment(endDate).format('DD-MMM-YY') || 'N/A'}`;
    worksheet.getCell('A3').font = { bold: true };

    // Define column structure without auto header row
    worksheet.columns = [
      { key: 'printName', width: 30 },
      { key: 'openingStock', width: 20 },
      { key: 'totalAdd', width: 20 },
      { key: 'totalLess', width: 20 },
      { key: 'closingStock', width: 20 },
      { key: 'uom', width: 10 },
    ];

    // Manually add header row at row 4
    const headerRow = worksheet.getRow(4);
    headerRow.values = [, 'Item', 'Opening Stock', 'Addition', 'Deduction', 'Closing Stock', 'UOM']; // 1-based indexing
    headerRow.font = { bold: true };
    headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

    // Add data rows starting from row 5
    reportData.forEach(row => {
      worksheet.addRow({
        printName: row.printName,
        openingStock: row.openingQty ?? 0,
        totalAdd: row.totalAdd ?? 0,
        totalLess: row.totalLess ?? 0,
        closingStock: row.closingQty ?? 0,
        uom: row.uom || '',
      });
    });

    // Apply border and alignment from header row onward
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber >= 4) {
        row.eachCell(cell => {
          cell.border = {
            top: { style: 'thin' },
            bottom: { style: 'thin' },
            left: { style: 'thin' },
            right: { style: 'thin' },
          };
          cell.alignment = { vertical: 'middle', horizontal: 'left' };
        });
      }
    });

    // Freeze top 4 rows
    worksheet.views = [{ state: 'frozen', ySplit: 4 }];

    // Export to Excel
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `EventWiseReport_${moment().format('YYYY-MM-DD')}.xlsx`);
  };

  return (
    <div className="formcontainer mt-3">
      <h5>Event Wise Report</h5>
      <div className="mb-4">
        <div className="row align-items-end mb-3">
          <div className="col-md-3">
            <label className="form-label">Event</label>
            <Select options={events} value={selectedEvent} onChange={setSelectedEvent} />
          </div>
          <div className="col-md-4">
            <label className="form-label">Department</label>
            <Select
              isMulti
              options={departments}
              value={selectedDept}
              onChange={setSelectedDept}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Date Range</label>
            <DateRangePickerComponent value={dateRange} onChange={setDateRange} />
          </div>
          <div className="col-md-2">
            <button
              className="btn btn-outline-success w-100"
              onClick={getReport}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Get Report'}
            </button>
          </div>
        </div>
      </div>

      <div className="row align-items-center mb-2">
        <div className="col-md-9">
          <input
            className="form-control"
            placeholder="Search..."
            onChange={e => setGlobalFilter(e.target.value)}
          />
        </div>
        {reportData.length > 0 && (
          <div className="col-md-3 text-end">
            <button className="btn btn-success w-100" onClick={exportToExcel}>
              Export to Excel
            </button>
          </div>
        )}
      </div>

      <table
        style={{ fontSize: '15px', verticalAlign: 'baseline' }}
        className="table table-striped table-bordered"
        {...getTableProps()}
      >
        <thead className="thead-dark">
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render('Header')}
                  <span>{column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}</span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map(row => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map(cell => (
                  <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="row mt-3 align-items-center">
        <div className="col-md-4 d-flex justify-content-start">
          <button className="btn btn-outline-secondary me-2" onClick={() => previousPage()} disabled={!canPreviousPage}>
            Previous
          </button>
        </div>
        <div className="col-md-4 d-flex flex-column align-items-center">
          <span>
            Page {state.pageIndex + 1} of {pageOptions.length}
          </span>
          <div className="d-flex align-items-center mt-1">
            <label className="me-2 mb-0">Rows per page:</label>
            <select
              className="form-select form-select-sm w-auto"
              value={state.pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
            >
              {[10, 20, 30, 40, 50].map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="col-md-4 d-flex justify-content-end">
          <button className="btn btn-outline-secondary" onClick={() => nextPage()} disabled={!canNextPage}>
            Next
          </button>
        </div>
      </div>

    </div>
  );
}

export default EventWiseReport;
